import { NavigateFunction } from 'react-router-dom';
import { ICreateReturnRequest, ICreateReturnShipment } from '@src/models/api';
import { IReturnShipments } from '@src/stores/returnStore';
import {
  ItemToReturn,
  OrderDetailsSessionDataV2,
  RefundValues,
  ReturnSummaryStorageData,
  ShippingInfoDict,
  SummaryShipment,
} from '../models/returns';
import { RETURN_SUMMARY_SESSION_STORAGE_KEY } from '../constants/general';
import { getWindow, roundValueAndTrim } from './general';
import { setSessionStorage } from './storage-helper';
import {
  getItemsByHrtvShipment,
  getLORPShipFromAddress,
  getSsReturnCreateShipFromAddress,
} from '.';
import { getItemToReturnItemsFromShipment } from './itemsToReturnUtils';

export const navigateToReturnSummaryPage = (navigate: NavigateFunction) => {
  if (getWindow()) {
    navigate('/myaccount/return-summary', { replace: true });
  }
};

export const persistReturnSummaryinfo = (
  returnSummaryData: ReturnSummaryStorageData,
  navigate: NavigateFunction,
) => {
  try {
    setSessionStorage(RETURN_SUMMARY_SESSION_STORAGE_KEY, returnSummaryData);
    navigateToReturnSummaryPage(navigate);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to set session storage:', error);
  }
};

export const getFinalizeReturnShipments = (
  emailId: string,
  deliveryDictionary: ShippingInfoDict,
  returnShipments: IReturnShipments[],
  selectedItems: ItemToReturn[],
  orderDetailsV2: OrderDetailsSessionDataV2,
): ICreateReturnRequest['shipments'] =>
  returnShipments?.map((shipment) => {
    const shipmentLineItems = getItemToReturnItemsFromShipment(
      shipment,
      selectedItems,
    );
    const totalWeight = shipmentLineItems.reduce(
      (acc, item) => acc + (item.itemWeight || 0) * (item.returnQuantity || 0),
      0,
    );
    const isLORP = shipment.shipmentType === 'PICKUP';
    const noOfBoxes = isLORP ? 1 : shipment.boxQuantity || 0;
    const shipFromAddress = isLORP
      ? getLORPShipFromAddress(
          shipment.lorpContactDetails || {},
          orderDetailsV2.fulfillmentGroups,
          selectedItems,
        )
      : getSsReturnCreateShipFromAddress(
          emailId,
          shipmentLineItems,
          deliveryDictionary,
        );
    return {
      itemWeight: roundValueAndTrim(totalWeight),
      mVendorNumber: shipmentLineItems[0]?.mVendorNumber || '',
      noOfBoxes,
      lineItems: shipmentLineItems.map(
        (item): ICreateReturnShipment['lineItems'][number] => {
          const hrtvLineItem = shipment.lineItems.find(
            (lineItem) => lineItem.lineItemId === item.lineItemId,
          );
          return {
            lineItemId: item.lineItemId || '',
            poNumber: item.poNumber || '',
            sku: (item.sku as string) || '',
            returnQuantity: item.returnQuantity,
            returnReasonCode: item.returnReasonCode,
            productType: item.productType,
            rtvTokenId: hrtvLineItem?.rtvTokenId || '',
            returnDispCode: hrtvLineItem?.returnDispCode || '',
            returnPolicyId: hrtvLineItem?.returnPolicyId || 0,
            itemDescription: item.itemDescription || '',
            hazmatItem: item.hazmatItem || false,
          };
        },
      ),
      shipFromAddress,
      shipToAddress: shipment.shipToAddress,
      shipmentType: shipment.shipmentType,
    };
  }) || [];

/**
 * Build return summary data for in-store returns
 */
export const buildInStoreReturnSummaryData = (
  orderNumber: string,
  emailId: string,
  selectedItems: ItemToReturn[],
  refundValues: RefundValues | undefined,
  returnShipments: IReturnShipments[],
): ReturnSummaryStorageData => ({
  orderId: orderNumber,
  emailId,
  refundValues,
  summaryShipments: returnShipments.map((shipment) => ({
    shipmentId: shipment.shipmentId,
    shipmentType: shipment.shipmentType,
    itemsToReturn: selectedItems,
    returnByDate:
      getItemsByHrtvShipment(shipment, selectedItems)?.[0]
        .returnWindowEndDate || '',
  })),
});

/**
 * Build return summary data for LORP/multi-address returns
 */
export const buildSummaryData = (
  orderNumber: string,
  emailId: string,
  refundValues: RefundValues | undefined,
  summaryShipments: SummaryShipment[],
  hasDuplicateReturns: boolean,
): ReturnSummaryStorageData => ({
  orderId: orderNumber,
  emailId,
  refundValues,
  summaryShipments,
  hasDuplicateReturns,
});
